import { ufToName } from 'shared/constants/brazilStates';

/**
 * Real-estate address formatting helpers, composing a display string from a
 * structured address object (see reference doc 03 §1.3 and §5.1).
 *
 * The address object accepts the field aliases found across the references:
 * `street`, `number` (or `streetNumber`), `neighborhood`, `city` and `state`
 * (UF acronym). Empty parts are dropped and trailing separators are cleaned up.
 */

/**
 * Composes a full address line, e.g.
 * `Avenida Iguaçu, 2195 - Água Verde, Curitiba - PR`.
 * @param {object} [address] - Structured address object.
 * @returns {string|null} The composed address, or null when no address is given.
 */
export const formatFullAddress = (address = {}) => {
  if (!address) return null;

  const number = address.streetNumber || address.number;
  const parts = [
    address.street && `${address.street}${number ? `, ${number}` : ''} -`,
    address.neighborhood && `${address.neighborhood},`,
    address.city && `${address.city} -`,
    address.state,
  ];

  return parts
    .filter(Boolean)
    .join(' ')
    .replace(/\s?[-,]$/, '')
    .trim();
};

/**
 * Composes a compact address from the two most specific non-empty parts, e.g.
 * `Curitiba. Água Verde` — useful for cards (see reference doc 03 §1.3).
 * @param {object} [address] - Structured address object.
 * @returns {string} The compact address (may be empty).
 */
export const formatShortAddress = ({
  state,
  city,
  neighborhood,
  street,
} = {}) =>
  [state, city, neighborhood, street].filter(Boolean).slice(-2).join('. ');

/**
 * Builds a human-readable address string suitable for a maps deep-link query,
 * preferring the full state name when only the UF acronym is available
 * (see reference doc 03 §5.1).
 * @param {object} [address] - Structured address object.
 * @returns {string} The address query string (may be empty).
 */
export const formatAddressForMaps = (address = {}) => {
  if (!address) return '';

  const number = address.streetNumber || address.number;
  const stateLabel = address.stateName || ufToName(address.state);

  return [
    address.street && `${address.street}${number ? `, ${number}` : ''}`,
    address.neighborhood,
    address.city,
    stateLabel,
  ]
    .filter(Boolean)
    .join(', ');
};
